/**
 * Cart Reward Cleanup - Removes reward products when their trigger products are removed
 *
 * This script monitors cart updates and automatically removes reward products
 * when their associated trigger products are no longer in the cart.
 */

class CartRewardCleanup {
  constructor() {
    this.isProcessing = false;
    this.init();
  }

  init() {
    // Listen for cart updates
    document.addEventListener('cart:updated', (event) => {
      this.handleCartUpdate(event);
    });

    console.log('CartRewardCleanup initialized');
  }

  async handleCartUpdate(event) {
    // Prevent concurrent processing
    if (this.isProcessing) {
      console.log('CartRewardCleanup: Already processing, skipping');
      return;
    }

    this.isProcessing = true;

    try {
      // Get current cart state
      const cartData = await this.fetchCart();

      // Check for orphaned reward products
      const rewardsToRemove = await this.findOrphanedRewards(cartData);

      if (rewardsToRemove.length > 0) {
        console.log('CartRewardCleanup: Found orphaned rewards:', rewardsToRemove);
        await this.removeOrphanedRewards(rewardsToRemove, cartData);
      }
    } catch (error) {
      console.error('CartRewardCleanup: Error processing cart update:', error);
    } finally {
      this.isProcessing = false;
    }
  }

  async fetchCart() {
    const response = await fetch('/cart.js');
    return response.json();
  }

  async findOrphanedRewards(cartData) {
    const orphanedRewards = [];

    // Get reward configuration from settings (we'll need to pass this from Liquid)
    const rewardConfig = window.cartRewardConfig || [];

    if (rewardConfig.length === 0) {
      return orphanedRewards;
    }

    // Build a map of trigger products currently in cart
    const triggerProductsInCart = new Set();
    cartData.items.forEach((item) => {
      triggerProductsInCart.add(item.product_id);
    });

    // Check each cart item to see if it's a reward product without its trigger
    cartData.items.forEach((item, index) => {
      // Check if this item is a reward product
      const rewardEntry = rewardConfig.find((config) => config.rewardProductId === item.product_id);

      if (rewardEntry) {
        // This is a reward product - check if its trigger is in cart
        if (!triggerProductsInCart.has(rewardEntry.triggerProductId)) {
          console.log(`CartRewardCleanup: Reward product ${item.product_id} has no trigger in cart`);
          orphanedRewards.push({
            lineIndex: index + 1, // Shopify uses 1-based indexing
            variantId: item.variant_id,
            productId: item.product_id,
            title: item.title,
            triggerProductId: rewardEntry.triggerProductId,
          });
        }
      }
    });

    return orphanedRewards;
  }

  async removeOrphanedRewards(rewardsToRemove, cartData) {
    console.log('CartRewardCleanup: Removing orphaned rewards...');

    // Show loading overlay
    const cartDrawer = document.querySelector('cart-drawer');
    if (cartDrawer && cartDrawer.showSimpleLoading) {
      cartDrawer.showSimpleLoading();
    }

    try {
      // Remove rewards one by one (in reverse order to maintain correct line indices)
      const sortedRewards = rewardsToRemove.sort((a, b) => b.lineIndex - a.lineIndex);

      for (const reward of sortedRewards) {
        console.log(`CartRewardCleanup: Removing reward at line ${reward.lineIndex}: ${reward.title}`);

        await fetch('/cart/change.js', {
          method: 'POST',
          headers: {
            'Content-Type': 'application/json',
          },
          body: JSON.stringify({
            line: reward.lineIndex,
            quantity: 0,
          }),
        });
      }

      // Refresh cart drawer after all removals
      const cartDrawerItems = document.querySelector('cart-drawer-items');
      if (cartDrawerItems && cartDrawerItems.onCartUpdate) {
        setTimeout(() => {
          cartDrawerItems.onCartUpdate();
        }, 200);
      } else if (cartDrawer && cartDrawer.hideSimpleLoading) {
        // Fallback: hide loading if cart drawer items not found
        setTimeout(() => {
          cartDrawer.hideSimpleLoading();
        }, 500);
      }

      console.log('CartRewardCleanup: Successfully removed orphaned rewards');
    } catch (error) {
      console.error('CartRewardCleanup: Error removing orphaned rewards:', error);

      // Hide loading on error
      if (cartDrawer && cartDrawer.hideSimpleLoading) {
        cartDrawer.hideSimpleLoading();
      }
    }
  }
}

// Initialize when DOM is ready
if (document.readyState === 'loading') {
  document.addEventListener('DOMContentLoaded', () => {
    window.cartRewardCleanup = new CartRewardCleanup();
  });
} else {
  window.cartRewardCleanup = new CartRewardCleanup();
}
